// The "+" under a day column and its compact Add Block popover. The popover adds one block to the
// day of its "+". An outside click or Esc closes it only while the form is empty. Cancel and the
// close icon always close it.
import { useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent, Ref } from "react";
import { Button, Form, Popover } from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { sameBlockContent } from "@utils/rtp_utils";
import { rtpApi, runRtpWrite } from "./rtpHooks";
import type { RtpBlockLibraryState } from "./rtpHooks";
import { BLOCK_CONTENT_FIELDS, BlockContentFields, buildBlockContent, hasBlockContent } from "./BlockForm";
import type { BlockFormValues } from "./BlockForm";
import { BlockLibraryPicker } from "./BlockLibraryPicker";
import { BlockLibraryStar } from "./BlockLibraryStar";
import type { BlockStarPlan } from "./BlockLibraryStar";
import type { RtpPhase } from "./types";

// The parent reads this handle from the open popover only.
export interface QuickAddHandle {
	hasContent: () => boolean;
	shake: () => void;
}

interface QuickAddBlockProps {
	phase: RtpPhase;
	dayIndex: number;
	// Enables the player-stats hint under the target rows.
	player?: { id: number; name: string };
	library: RtpBlockLibraryState;
	open: boolean;
	// The "+" was clicked while the popover was closed.
	onOpen: () => void;
	onClose: () => void;
	refetch: () => void;
	ref?: Ref<QuickAddHandle>;
}

const SHAKE_MS = 350;

// The gap to the window edge, and the limits of the form height.
const EDGE_MARGIN = 16;
const FORM_MIN_PX = 240;
const FORM_MAX_PX = 480;
// The popover width. The CSS rule of `.rtp-quick-add-pop` sets the same value.
const POPOVER_WIDTH_PX = 400;
// Measured height of the parts of the popover that do not scroll: the padding, the head row, the
// foot row, the form padding, and the gap between the popover and the "+".
const FIXED_PARTS_PX = 112;
// The CSS variable that adds temporary space at the bottom of the planner page. The padding of
// `.rtp-scope` reads it. One popover is open at a time, so one shared variable is enough.
const RESERVE_VAR = "--rtp-quick-add-reserve";

// The popup is not in the page yet when the ref runs, so the focus waits one task. The focus does
// not scroll: the popup starts outside the viewport.
const focusTextarea = (el: HTMLElement | null) => {
	if (el) setTimeout(() => el.querySelector("textarea")?.focus({ preventScroll: true }));
};

export const QuickAddBlock = ({
	phase,
	dayIndex,
	player,
	library,
	open,
	onOpen,
	onClose,
	refetch,
	ref,
}: QuickAddBlockProps) => {
	const [form] = Form.useForm<BlockFormValues>();
	const [submitting, setSubmitting] = useState(false);
	const [contentError, setContentError] = useState(false);
	const [shaking, setShaking] = useState(false);
	// The horizontal alignment to the "+", and the form height that fits below it. Set at each open.
	const [fit, setFit] = useState({ placement: "bottomLeft" as "bottomLeft" | "bottomRight", formMax: FORM_MAX_PX });
	const addRef = useRef<HTMLButtonElement>(null);
	// The library entry the user picked or saved with the star. ADD_BLOCK sends it as `templateId`.
	const [templateId, setTemplateId] = useState<string | null>(null);
	const watched = Form.useWatch([], form);
	// A deleted entry is not in the list: ADD_BLOCK gets a 400 for its id, so the popover drops it.
	const entry = library.templates.find((item) => item.id === templateId);
	// The star is filled only while the form still holds the content of the entry. An edited copy
	// keeps `templateId` for ADD_BLOCK, and a click on the empty star saves it as a new entry.
	const starEntryId = entry && sameBlockContent(buildBlockContent(watched ?? {}), entry.content) ? entry.id : null;

	const hasContent = () => hasBlockContent(buildBlockContent(form.getFieldsValue()));
	useImperativeHandle(ref, () => ({ hasContent, shake: () => setShaking(true) }));

	// Each open starts with an empty form.
	useEffect(() => {
		if (!open) return;
		form.resetFields();
		setContentError(false);
		setTemplateId(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// The popover always opens below the "+". When the room below is too small for the minimum
	// form height, the window scrolls down once. Near the end of the page the scroll stops short,
	// so the page gets temporary space at its bottom first. The popover keeps its placement and its
	// top edge while the form grows, so it does not move. The check runs before the first paint of
	// the popover.
	useLayoutEffect(() => {
		if (!open || !addRef.current) return;
		const button = addRef.current;
		const root = document.documentElement;
		const spaceBelow = () => window.innerHeight - button.getBoundingClientRect().bottom - EDGE_MARGIN;
		const missing = FIXED_PARTS_PX + FORM_MIN_PX - spaceBelow();
		if (missing > 0) {
			const scrollable = root.scrollHeight - window.innerHeight - window.scrollY;
			if (missing > scrollable) root.style.setProperty(RESERVE_VAR, `${Math.ceil(missing - scrollable)}px`);
			window.scrollBy({ top: missing, behavior: "instant" });
		}
		const left = button.getBoundingClientRect().left;
		const overflowsRight = left + POPOVER_WIDTH_PX > root.clientWidth - EDGE_MARGIN;
		setFit({
			placement: overflowsRight ? "bottomRight" : "bottomLeft",
			formMax: Math.min(FORM_MAX_PX, Math.max(FORM_MIN_PX, spaceBelow() - FIXED_PARTS_PX)),
		});
		return () => {
			root.style.removeProperty(RESERVE_VAR);
		};
	}, [open]);

	useEffect(() => {
		if (!shaking) return;
		const timer = setTimeout(() => setShaking(false), SHAKE_MS);
		return () => clearTimeout(timer);
	}, [shaking]);

	// The popup closes on Esc only when the focus is on the "+", so the page listens as well.
	useEffect(() => {
		if (!open) return;
		const onKeyDown = (e: globalThis.KeyboardEvent) => {
			if (e.key === "Escape" && !hasContent()) onClose();
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// Called for a click on the "+", an outside click, and Esc on the "+".
	const handleOpenChange = (next: boolean) => {
		if (next) onOpen();
		else if (open && !hasContent()) onClose();
	};

	const submit = async () => {
		if (submitting) return;
		const noContent = !hasContent();
		setContentError(noContent);
		let values: BlockFormValues;
		try {
			values = await form.validateFields();
		} catch {
			return;
		}
		if (noContent) return;
		setSubmitting(true);
		const content = buildBlockContent(values);
		const ok = await runRtpWrite(
			() =>
				rtpApi.rtpPhaseAction(phase.id, {
					action: "ADD_BLOCK",
					dayIndexes: [dayIndex],
					content,
					templateId: entry?.id,
				}),
			() => {
				refetch();
				// The block is a new use of the entry, so the usage counts change.
				if (entry) library.reload();
			}
		);
		setSubmitting(false);
		if (ok) onClose();
	};

	// The star needs content and valid rows.
	const prepareStar = async (): Promise<BlockStarPlan> => {
		const noContent = !hasContent();
		setContentError(noContent);
		if (noContent) return null;
		try {
			await form.validateFields(BLOCK_CONTENT_FIELDS, { recursive: true });
		} catch {
			return null;
		}
		return { content: buildBlockContent(form.getFieldsValue()) };
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key !== "Enter" || !(e.ctrlKey || e.metaKey)) return;
		e.preventDefault();
		submit();
	};

	const panel = (
		<div ref={focusTextarea} className="rtp-quick-add" onKeyDown={handleKeyDown}>
			<div className="rtp-quick-add-head">
				<span className="rtp-quick-add-title">Add Block</span>
				<BlockLibraryPicker compact templates={library.templates} form={form} onPick={setTemplateId} />
				<span className="rtp-quick-add-spacer" />
				<BlockLibraryStar
					library={library}
					templateId={starEntryId}
					readOnly={!library.canCreate}
					prepare={prepareStar}
					onLinkChange={setTemplateId}
				/>
				<Button
					type="text"
					size="small"
					icon={<CloseOutlined />}
					aria-label="Close"
					data-attr="settings.rtp-block.quick-add-close"
					onClick={onClose}
				/>
			</div>
			<Form
				form={form}
				layout="vertical"
				className="rtp-quick-add-form"
				style={{ "--rtp-quick-add-form-max": `${fit.formMax}px` } as CSSProperties}
			>
				<BlockContentFields player={player} contentError={contentError} rows={2} />
			</Form>
			<div className="rtp-quick-add-foot">
				<Button data-attr="settings.rtp-block.quick-add-cancel" onClick={onClose}>
					Cancel
				</Button>
				<Button
					type="primary"
					className="rtp-wz-primary-btn"
					loading={submitting}
					data-attr="settings.rtp-block.quick-add-submit"
					onClick={submit}
				>
					Add
				</Button>
			</div>
		</div>
	);

	return (
		<Popover
			open={open}
			trigger="click"
			placement={fit.placement}
			autoAdjustOverflow={false}
			destroyOnHidden
			// Below antd Modal (1000): a dialog opened from the toolbar covers this popover. The
			// popups inside it (Select, picker) take their z-index from this one and stay above it.
			zIndex={999}
			content={panel}
			onOpenChange={handleOpenChange}
			// The popup portals outside .rtp-scope — rtp-wz carries the design tokens there.
			classNames={{ root: `rtp-wz rtp-quick-add-pop${shaking ? " rtp-quick-add-pop--shake" : ""}` }}
		>
			<Button
				ref={addRef}
				className={`rtp-block-add${open ? " rtp-block-add--open" : ""}`}
				type="dashed"
				size="small"
				icon={<PlusOutlined />}
				aria-label={`Add block for day ${dayIndex}`}
				data-attr="settings.rtp-block.add-inline"
				// A click on the "+" of an open popover with content keeps it open and shakes it.
				onClick={() => {
					if (open && hasContent()) setShaking(true);
				}}
			/>
		</Popover>
	);
};
